// src/pages/admin/AdminUserManagement.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { userService, companyService } from '../../services/apiServices';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import RoleBadge from '../../components/admin/RoleBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import Pagination from '../../components/admin/Pagination';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showWarningToast } from '../../utils/toastUtils';
import UserLimitsDashboard from '../../components/admin/UserLimitsDashboard';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';


const formVariants = {
  hidden: { opacity: 0, y: -30, scale: 0.97 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 28 }
  },
  exit: {
    opacity: 0, y: -20, scale: 0.98,
    transition: { duration: 0.15, ease: 'easeIn' }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.25, ease: 'easeOut' }
  })
};

const AdminUserManagement = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const formRef = useRef(null);

  // State for users
  const [users, setUsers] = useState([]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'simpleuser',
    status: 'active',
    mfaRequired: false
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showForceLogoutDialog, setShowForceLogoutDialog] = useState(false);
  const [userToForceLogout, setUserToForceLogout] = useState(null);

  // Company quotas (set by superadmin) + usage counts per role
  const [companyQuotas, setCompanyQuotas] = useState(null);
  const [companyUsage, setCompanyUsage] = useState({ admins: 0, analysts: 0, viewers: 0 });

  // Scroll to form when it opens
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm]);

  // Fetch users on component mount or when filters change
  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, searchTerm, roleFilter, statusFilter]);

  // Fetch company quotas + usage counts (separate from paginated table)
  useEffect(() => {
    const fetchQuotas = async () => {
      if (!currentUser?.companyId) return;
      try {
        const companyRes = await companyService.getCompanyById(currentUser.companyId);
        if (companyRes.success) {
          setCompanyQuotas(companyRes.data?.quotas || null);
        }
      } catch (e) {
        // Non-blocking: quotas stay null (= unlimited display)
      }
      try {
        const countsRes = await userService.getUsers({ companyId: currentUser.companyId, limit: 1000, page: 1 });
        if (countsRes.success) {
          const all = countsRes.data || [];
          setCompanyUsage({
            admins: all.filter(u => u.role === 'admin').length,
            analysts: all.filter(u => u.role === 'simpleuser').length,
            viewers: all.filter(u => u.role === 'viewer').length
          });
        }
      } catch (e) {
        // Non-blocking
      }
    };
    fetchQuotas();
  }, [currentUser?.companyId, users.length]);

  // Function to fetch users from API (company-scoped)
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare filters for API
      const filters = {
        companyId: currentUser.companyId, // Only get users from current admin's company
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (roleFilter !== 'all') filters.role = roleFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      const response = await userService.getUsers(filters);

      if (response.success) {
        setUsers(response.data || []);
        // Update pagination from response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.pagination
          }));
        } else {
          // Fallback
          setPagination(prev => ({
            ...prev,
            total: response.data?.length || 0,
            pages: Math.ceil((response.data?.length || 0) / prev.limit)
          }));
        }
      } else {
        throw new Error(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Erreur lors du chargement des utilisateurs. Veuillez réessayer plus tard.');
      showErrorToast('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort users
  // Note: Filtering is now largely handled on the server side
  const filteredUsers = [...users]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'lastLogin' || sortConfig.key === 'createdAt') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const totalUsersMetric = pagination.total || users.length;
  const activeUsers = users.filter(user => user.status === 'active').length;
  const inactiveUsers = users.filter(user => user.status === 'inactive').length;
  const adminCount = users.filter(user => user.role === 'admin').length;
  const analysts = users.filter(user => user.role === 'simpleuser').length;
  const viewers = users.filter(user => user.role === 'viewer').length;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      showWarningToast('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      showWarningToast('L\'email est requis');
      return;
    }
    if (!isEditing && !formData.password.trim()) {
      showWarningToast('Le mot de passe est requis');
      return;
    }
    if ((!isEditing || formData.password.trim()) && !validatePassword(formData.password).isValid) {
      showWarningToast('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole');
      return;
    }

    const action = isEditing ? 'Mise à jour' : 'Création';
    const loadingToastId = showLoadingToast(`${action} de l'utilisateur en cours...`);

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare data for API - include company ID
      const userData = {
        ...formData,
        companyId: currentUser.companyId
      };

      // Client-side company quota pre-check (server re-checks authoritatively)
      if (!isEditing && companyQuotas) {
        const quotaByRole = { admin: companyQuotas.maxAdmins, simpleuser: companyQuotas.maxSimpleUsers, viewer: companyQuotas.maxViewers };
        const usageByRole = { admin: companyUsage.admins, simpleuser: companyUsage.analysts, viewer: companyUsage.viewers };
        const cap = quotaByRole[userData.role];
        if (cap !== null && cap !== undefined && cap !== '' && (usageByRole[userData.role] ?? 0) >= Number(cap)) {
          throw new Error(`Quota entreprise atteint (${usageByRole[userData.role]}/${cap}). Demandez une augmentation au superadmin.`);
        }
      }

      if (isEditing) {
        // For updates, handle password separately if provided
        const hasPasswordUpdate = userData.password && userData.password.trim();

        // Remove password from main user data - it must go through /password endpoint
        delete userData.password;

        // Update existing user
        const response = await userService.updateUser(currentUserId, userData);

        if (response.success) {
          // If password was provided, update it separately
          if (hasPasswordUpdate) {
            try {
              await userService.updatePassword(currentUserId, {
                newPassword: formData.password
              });
            } catch (passwordError) {
              console.error('Password update error:', passwordError);
              updateToast(loadingToastId, 'Utilisateur mis à jour, mais erreur lors du changement de mot de passe', 'warning');
              resetForm();
              return;
            }
          }

          // Update the user in the local state
          setUsers(
            users.map((user) =>
              user._id === currentUserId
                ? { ...user, ...response.data }
                : user
            )
          );

          updateToast(loadingToastId, 'Utilisateur mis à jour avec succès', 'success');
          resetForm();
        } else {
          throw new Error(response.message || 'Failed to update user');
        }
      } else {
        // Create new user
        const response = await userService.createUser(userData);

        if (response.success) {
          // Add the new user to the local state
          setUsers([...users, response.data]);
          updateToast(loadingToastId, 'Utilisateur créé avec succès', 'success');
          resetForm();

          // Refresh current user data to update limits
          if (refreshUser) {
            await refreshUser();
          }
        } else {
          throw new Error(response.message || 'Failed to create user');
        }
      }
    } catch (error) {
      console.error(isEditing ? 'Update user error:' : 'Create user error:', error);
      const errorMessage = `Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'} de l'utilisateur. ${error.message || 'Veuillez réessayer.'}`;
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    if (!user || typeof user !== 'object') {
      console.error('Invalid user object:', user);
      return;
    }

    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Clear password field when editing
      role: user.role || 'simpleuser',
      status: user.status || 'active',
      mfaRequired: user.mfa?.requiredByAdmin || false
    });

    setCurrentUserId(user._id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleStatusToggle = async (user) => {
    const action = user.status === 'active' ? 'désactiver' : 'activer';
    const loadingToastId = showLoadingToast(`${action.charAt(0).toUpperCase() + action.slice(1)} l'utilisateur...`);

    setIsSubmitting(true);

    try {
      const updatedStatus = user.status === 'active' ? 'inactive' : 'active';

      const response = await userService.updateUser(user._id, { status: updatedStatus });

      if (response.success) {
        setUsers(
          users.map((u) =>
            u._id === user._id
              ? { ...u, status: updatedStatus }
              : u
          )
        );
        updateToast(loadingToastId, `Utilisateur ${updatedStatus === 'active' ? 'activé' : 'désactivé'} avec succès`, 'success');
      } else {
        throw new Error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Update user status error:', error);
      const errorMessage = 'Erreur lors de la mise à jour du statut. Veuillez réessayer.';
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirmation(true);
  };

  const handleForceLogout = (user) => {
    setUserToForceLogout(user);
    setShowForceLogoutDialog(true);
  };

  const handleForceLogoutConfirm = async () => {
    if (!userToForceLogout) return;

    setShowForceLogoutDialog(false);

    const loadingToastId = showLoadingToast('Déconnexion en cours...');

    try {
      const response = await userService.forceLogout(userToForceLogout._id);

      if (response.success) {
        updateToast(
          loadingToastId,
          response.notConnected ? 'Cet utilisateur n\'est pas connecté' : 'Session terminée avec succès',
          response.notConnected ? 'warning' : 'success'
        );
      } else {
        throw new Error(response.message || 'Failed to force logout');
      }
    } catch (error) {
      console.error('Force logout error:', error);
      updateToast(loadingToastId, 'Erreur lors de la déconnexion forcée', 'error');
    } finally {
      setUserToForceLogout(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsSubmitting(true);
    setShowConfirmation(false);

    try {
      const response = await userService.deleteUser(userToDelete._id);

      if (response.success) {
        // Remove the user from the local state
        setUsers(users.filter(u => u._id !== userToDelete._id));
        setUserToDelete(null);
        showSuccessToast('Utilisateur supprimé avec succès');

        // Refresh current user data to update limits
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMessage = 'Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer.';
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser',
      status: 'active',
      mfaRequired: false
    });
    setIsEditing(false);
    setCurrentUserId(null);
    setShowForm(false);
    setError(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    switch (key) {
      case 'role':
        setRoleFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value) => <span className="font-medium text-gray-900 text-sm">{value || '-'}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || '-'}</span>
    },
    {
      key: 'role',
      label: 'Rôle',
      sortable: true,
      render: (value) => <RoleBadge role={value} size="sm" />
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => <StatusBadge status={value === 'active' ? 'Actif' : 'Inactif'} type={value === 'active' ? 'success' : 'danger'} size="sm" />
    },
    {
      key: 'cguAccepted',
      label: 'CGU',
      sortable: true,
      render: (value) => (
        <div className="flex items-center justify-center text-sm font-bold" title={value ? 'Conditions acceptées' : 'Conditions non acceptées'}>
          {value ? (
            <span className="text-green-700 bg-green-100 border border-green-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">✅</span> Oui
            </span>
          ) : (
            <span className="text-gray-600 bg-gray-100 border border-gray-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">❌</span> Non
            </span>
          )}
        </div>
      )
    },
    {
      key: 'lastLogin',
      label: 'Dernière connexion',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value ? new Date(value).toLocaleString() : 'Jamais'}</span>
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value ? new Date(value).toLocaleDateString('fr-FR') : '-'}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, user) => (
        <div className="flex flex-wrap gap-1">
          <motion.button
            onClick={() => handleEdit(user)}
            className="text-xs text-indigo-600 hover:text-white hover:bg-indigo-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={isSubmitting}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fas fa-edit text-xs"></i>
            <span>Modifier</span>
          </motion.button>

          <button
            onClick={() => handleStatusToggle(user)}
            className={`text-xs font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1 ${user.status === 'active'
              ? 'text-red-600 hover:text-white hover:bg-red-600'
              : 'text-green-600 hover:text-white hover:bg-green-600'
              }`}
            disabled={isSubmitting}
          >
            <i className={`fas text-xs ${user.status === 'active' ? 'fa-ban' : 'fa-check'}`}></i>
            <span>{user.status === 'active' ? 'Désactiver' : 'Activer'}</span>
          </button>

          <button
            onClick={() => handleForceLogout(user)}
            className="text-xs text-amber-600 hover:text-white hover:bg-amber-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={isSubmitting || user._id === currentUser?.id}
            title={user._id === currentUser?.id ? 'Vous ne pouvez pas déconnecter votre propre session' : 'Forcer la déconnexion'}
          >
            <i className="fas fa-sign-out-alt text-xs"></i>
            <span>Déconnecter</span>
          </button>

          <button
            onClick={() => handleDeleteClick(user)}
            className="text-xs text-red-600 hover:text-white hover:bg-red-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={isSubmitting}
          >
            <i className="fas fa-trash text-xs"></i>
            <span>Supprimer</span>
          </button>
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Utilisateurs
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Administration des utilisateurs de votre entreprise</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all duration-200 flex items-center space-x-2 ${showForm
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-indigo-600 hover:bg-indigo-700'
            } focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={isSubmitting}
        >
          <i className={`fas ${showForm ? 'fa-times' : 'fa-user-plus'} text-sm`}></i>
          <span>{showForm ? 'Annuler' : 'Nouvel Utilisateur'}</span>
        </button>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{totalUsersMetric}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actifs</p>
          <p className="text-2xl font-bold text-green-600 mt-0.5">{activeUsers}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Administrateurs</p>
          <p className="text-2xl font-bold text-purple-600 mt-0.5">{adminCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Analystes</p>
          <p className="text-2xl font-bold text-yellow-600 mt-0.5">{analysts}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-3">
          <i className="fas fa-exclamation-circle text-red-500"></i>
          <p className="text-sm flex-1">{error}</p>
          <button
            onClick={fetchUsers}
            className="text-sm bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 transition-colors"
          >
            <i className="fas fa-redo mr-1.5"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* User Creation Limits Dashboard */}
      <UserLimitsDashboard
        currentUser={currentUser}
        companyQuotas={companyQuotas}
        companyUsage={companyUsage}
        onRequestSubmitted={() => {
          // Optionally refresh user data or show a success message
          showSuccessToast('Votre demande a été envoyée et sera examinée par le superadmin');
        }}
        onRefreshLimits={refreshUser}
      />

      {/* User Form with Animations */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            key="user-form"
            ref={formRef}
            variants={formVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 relative"
          >
            {isSubmitting && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent"></div>
                  <p className="text-sm text-gray-600">Traitement en cours...</p>
                </div>
              </div>
            )}

            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                <i className={`fas ${isEditing ? 'fa-edit' : 'fa-user-plus'} text-indigo-600 mr-2`}></i>
                {isEditing ? 'Modifier l\'utilisateur' : 'Créer un nouvel utilisateur'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Personal Information */}
              <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Informations personnelles</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nom complet *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                      disabled={isSubmitting}
                      placeholder="Entrez le nom complet"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                      disabled={isSubmitting}
                      placeholder="utilisateur@exemple.com"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Security & Access */}
              <motion.div custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sécurité et accès</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {isEditing ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe *'}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required={!isEditing}
                      disabled={isSubmitting}
                      placeholder={isEditing ? "Laisser vide pour ne pas changer" : "Mot de passe sécurisé"}
                    />
                    {(!isEditing || formData.password) && (
                      <PasswordRequirements password={formData.password} />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="admin">Administrateur</option>
                      <option value="simpleuser">Analyste</option>
                      <option value="viewer">Lecteur (lecture seule)</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Status */}
              <motion.div custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Statut du compte</h3>
                </div>
                <div className="p-4 flex items-center gap-4">
                  <div className="w-48">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* MFA obligatoire */}
              <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sécurité</h3>
                </div>
                <div className="p-4">
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer py-2.5 px-4 rounded-lg border border-gray-200 hover:border-indigo-300 transition-all duration-200 w-full bg-white/50 hover:bg-white">
                      <input
                        type="checkbox"
                        name="mfaRequired"
                        checked={formData.mfaRequired}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm font-semibold text-gray-700">MFA obligatoire</span>
                        <p className="text-xs text-gray-400 mt-0.5">Configurer à la prochaine connexion</p>
                      </div>
                    </label>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times mr-1.5"></i>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className={`fas ${isEditing ? 'fa-save' : 'fa-user-plus'} text-xs`}></i>
                      {isEditing ? 'Mettre à jour' : 'Créer l\'utilisateur'}
                    </>
                  )}
                </button>
              </motion.div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search and filters */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'role',
            label: 'Filtrer par rôle',
            type: 'select',
            value: roleFilter,
            options: [
              { value: 'all', label: 'Tous les rôles' },
              { value: 'admin', label: 'Administrateur' },
              { value: 'simpleuser', label: 'Analyste' },
              { value: 'viewer', label: 'Lecteur' }
            ]
          },
          {
            key: 'status',
            label: 'Filtrer par statut',
            type: 'select',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        placeholder="Rechercher par nom ou email..."
      />

      {/* Data table */}
      <DataTable
        data={filteredUsers}
        columns={columns}
        isLoading={isLoading}
        onSort={handleSort}
        sortConfig={sortConfig}
        emptyMessage="Aucun utilisateur trouvé."
        searchQuery={searchTerm}
        filters={{ role: roleFilter, status: statusFilter }}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Results summary */}
      {filteredUsers.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
          Affichage de {filteredUsers.length} utilisateur(s) sur {pagination.total} au total
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        confirmButtonClass="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        cancelButtonClass="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      />

      {/* Force Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showForceLogoutDialog}
        onClose={() => {
          setShowForceLogoutDialog(false);
          setUserToForceLogout(null);
        }}
        onConfirm={handleForceLogoutConfirm}
        title="Forcer la déconnexion"
        message={`Êtes-vous sûr de vouloir déconnecter "${userToForceLogout?.name}" de toutes ses sessions actives ?`}
        confirmText="Déconnecter"
        cancelText="Annuler"
        confirmButtonClass="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
        cancelButtonClass="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      />
    </div>
  );
};

export default AdminUserManagement;